import cn from "@/v2-ui/utils/utils.cn"
import Link from "@/v2-backoffice-client/components/link"
import { formatCompactAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import type { LicensePlanFragment } from "@/v2-backoffice-client/license/plan/__types__/license.plan.fragment"

// How many catalog rows the comparison shows before collapsing the tail —
// enough to see the neighbourhood, few enough to read at a glance.
const COMPARISON_ROWS = 7

export type LicensePlanComparisonProps = {
  plan: LicensePlanFragment
  /** The billing catalog, biggest book first (derived by the Revenue tab). */
  rankedPlans: LicensePlanFragment[]
  currency?: string
}

// The Revenue tab's "License vs. catalog" section: the top of the billing
// catalog as MRR bars, this license highlighted (and appended when it ranks
// below the cutoff). Only rendered for billing licenses — comparing a
// non-billing license against the catalog is a wall of other licenses' money, and
// the rank stat card already says "Unranked" and why.
function LicensePlanComparison(props: LicensePlanComparisonProps) {
  const { plan, rankedPlans, currency } = props

  const comparisonRows = rankedPlans.slice(0, COMPARISON_ROWS)
  if (!comparisonRows.some((candidate) => candidate.Id === plan.Id)) {
    comparisonRows.push(plan)
  }
  const maxComparisonMrr = comparisonRows[0]?.Mrr ?? 0

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-baseline gap-2">
        <h3 className="text-sm font-semibold">License vs. catalog</h3>
        <span className="text-xs text-muted-foreground">
          live MRR, biggest book first
        </span>
      </div>

      <div className="flex flex-col gap-1">
        {comparisonRows.map((candidate) => {
          const isCurrent = candidate.Id === plan.Id
          const candidateMrr = candidate.Mrr ?? 0
          const widthPct =
            maxComparisonMrr > 0
              ? Math.max(2, (candidateMrr / maxComparisonMrr) * 100)
              : 0
          const candidateRank =
            rankedPlans.findIndex((ranked) => ranked.Id === candidate.Id) + 1

          return (
            <div
              key={candidate.Id}
              className={cn(
                "grid grid-cols-[minmax(0,220px)_1fr_110px] items-center gap-3 rounded-sm px-2 py-1",
                isCurrent && "bg-accent/50",
              )}
            >
              <span className="flex min-w-0 items-baseline gap-1.5 text-xs">
                <span className="w-6 shrink-0 tabular-nums text-muted-foreground">
                  {candidateRank > 0 ? `#${candidateRank}` : "—"}
                </span>
                {isCurrent ? (
                  <span className="truncate font-semibold">
                    {candidate.Name}
                  </span>
                ) : (
                  <Link
                    variant="muted"
                    iconSrc={null}
                    className="truncate"
                    to={`?v=plan:${candidate.Id}`}
                  >
                    {candidate.Name}
                  </Link>
                )}
              </span>

              {/* /30 fill on a muted track is nearly invisible in dark
                  mode — the empty track then reads as a full bar. */}
              <span className="h-2 overflow-hidden rounded-full bg-muted/40">
                <span
                  className={cn(
                    "block h-full rounded-full",
                    isCurrent ? "bg-primary" : "bg-muted-foreground/60",
                  )}
                  style={{ width: `${widthPct}%` }}
                />
              </span>

              <span className="text-right text-xs tabular-nums text-muted-foreground">
                {formatCompactAmount(candidateMrr, currency)}/mo
              </span>
            </div>
          )
        })}
      </div>

      {rankedPlans.length > comparisonRows.length && (
        <p className="text-xs text-muted-foreground">
          {rankedPlans.length - comparisonRows.length} more billing license
          {rankedPlans.length - comparisonRows.length === 1 ? "" : "s"} not
          shown — the full catalog lives on the Licenses board.
        </p>
      )}
    </section>
  )
}

export default LicensePlanComparison
